<template>
  <div
    class="counseling-admin-container"
    :style="chatViewportHeight ? { '--admin-chat-shell-height': `${chatViewportHeight}px` } : null"
  >
    <!-- Header -->
    <div class="admin-header">
      <div class="header-left">
        <h4 class="header-title">
          <i class="fas fa-headset text-success" />
          Counseling Queue
        </h4>
        <p class="header-subtitle">
          Manage support requests
        </p>
      </div>
      <div class="header-stats">
        <div class="stat-item">
          <span class="stat-label">Pending</span>
          <span class="stat-value">{{ pendingQueue.length }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Active</span>
          <span class="stat-value">{{ activeSessions.length }}</span>
        </div>
        <BButton
          size="sm"
          variant="outline-success"
          class="review-btn"
          @click="openTranscriptReview"
        >
          <i class="fas fa-archive me-1" />
          Saved Conversations
        </BButton>
      </div>
    </div>

    <div class="admin-layout">
      <!-- Left Panel: Pending Queue -->
      <div class="queue-panel">
        <div class="panel-header">
          <h5 class="panel-title">
            <i class="fas fa-clock text-warning" />
            Pending Requests
          </h5>
          <span class="pending-badge">{{ pendingQueue.length }}</span>
        </div>

        <div class="pending-list">
          <div
            v-if="!pendingQueue.length"
            class="empty-queue"
          >
            <i class="fas fa-check-circle" />
            <p>All caught up! No pending requests.</p>
          </div>

          <div
            v-for="session in pendingQueue"
            :key="`pending-${session.id}`"
            :class="['pending-item', { disabled: hasActiveSession }]"
          >
            <div class="pending-header">
              <div class="student-info">
                <span class="student-name">{{ studentLabel(session.student) }}</span>
                <span
                  v-if="session.queue_position"
                  class="queue-badge"
                >
                  #{{ session.queue_position }}
                </span>
              </div>
              <span class="pending-time">{{ timeAgo(session.created_at) }}</span>
            </div>

            <div class="pending-details">
              <div
                v-if="session.student"
                class="detail-row"
              >
                <span class="detail-label">ID:</span>
                <span class="detail-value">{{ session.student.id_number }}</span>
              </div>
              <div
                v-if="session.student"
                class="detail-row"
              >
                <span class="detail-label">Course:</span>
                <span class="detail-value">{{ session.student.course }}</span>
              </div>
            </div>

            <BButton
              class="accept-btn"
              size="sm"
              variant="success"
              :disabled="busy.acceptingId === session.id || hasActiveSession"
              :title="hasActiveSession ? 'You can only handle one session at a time' : ''"
              @click="accept(session)"
            >
              <i
                :class="busy.acceptingId === session.id ? 'fas fa-spinner fa-spin' : 'fas fa-check'"
                class="me-1"
              />
              {{ busy.acceptingId === session.id ? 'Accepting...' : 'Accept' }}
            </BButton>
          </div>
        </div>
      </div>

      <!-- Right Panel: Active Sessions & Chat -->
      <div class="chat-panel">
        <div
          v-if="!selectedSession"
          class="no-selection"
        >
          <i class="fas fa-comments" />
          <h5>No Session Selected</h5>
          <p>Select an active session or accept a pending request to begin</p>
        </div>

        <div
          v-else
          class="session-chat"
        >
          <!-- Session Header -->
          <div class="session-header">
            <div class="session-info">
              <h5 class="session-title">
                {{ studentLabel(selectedSession.student) }}
              </h5>
              <p class="session-subtitle">
                <i
                  class="fas fa-circle text-success me-1"
                  style="font-size: 0.5rem;"
                />
                Active
              </p>
            </div>
            <BButton
              size="sm"
              variant="outline-danger"
              :disabled="busy.closing"
              @click="closeSelected"
            >
              <i
                :class="busy.closing ? 'fas fa-spinner fa-spin' : 'fas fa-sign-out-alt'"
                class="me-1"
              />
              End Session
            </BButton>
          </div>

          <!-- Messages -->
          <div
            ref="chatMessages"
            class="chat-messages"
          >
            <div
              v-if="!selectedMessages.length"
              class="messages-empty"
            >
              <i class="fas fa-comments text-muted" />
              <p class="text-muted">
                No messages yet in this session
              </p>
            </div>

            <div
              v-else
              class="messages-list"
            >
              <div
                v-for="msg in selectedMessages"
                :key="msg.id"
                :class="['message-item', msg.sender_type === 'admin' ? 'sent' : 'received']"
              >
                <div class="message-bubble">
                  <div class="bubble-header">
                    <span class="bubble-sender">{{ msg.sender_name }}</span>
                    <span class="bubble-time">{{ formatTime(msg.sent_at) }}</span>
                  </div>
                  <div
                    v-if="msg.file_info"
                    class="bubble-file"
                  >
                    <div
                      v-if="isImageAttachment(msg.file_info)"
                      class="image-preview-wrapper"
                    >
                      <img
                        v-if="imagePreviewUrl(msg.file_info)"
                        :src="imagePreviewUrl(msg.file_info)"
                        :alt="displayFileName(msg.file_info)"
                        class="image-preview"
                        @click="openImagePreview(msg.file_info)"
                      >
                      <div
                        v-else
                        class="image-preview-loading"
                      >
                        <i class="fas fa-image" />
                        Loading preview...
                      </div>
                    </div>

                    <div class="file-row">
                      <i
                        :class="getFileIcon(msg.file_info.mime_type)"
                        class="file-type-icon"
                      />
                      <a
                        href="#"
                        class="file-link"
                        :title="extractFileName(msg.file_info)"
                        @click.prevent="downloadAttachment(msg.file_info)"
                      >
                        {{ displayFileName(msg.file_info) }}
                      </a>
                      <span
                        v-if="msg.file_info.size"
                        class="file-size"
                      >
                        {{ formatFileSize(msg.file_info.size) }}
                      </span>
                      <button
                        type="button"
                        class="download-btn"
                        :title="`Download ${extractFileName(msg.file_info)}`"
                        @click.prevent="downloadAttachment(msg.file_info)"
                      >
                        <i class="fas fa-download" />
                      </button>
                    </div>
                  </div>
                  <div
                    v-if="msg.message"
                    class="bubble-text"
                  >
                    {{ msg.message }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Input -->
          <div class="chat-input">
            <div class="input-wrapper">
              <div class="attach-upload">
                <input
                  ref="fileInput"
                  type="file"
                  class="file-input-hidden"
                  @change="onFileSelected"
                >
                <BButton
                  size="sm"
                  variant="outline-secondary"
                  :disabled="busy.uploading"
                  class="attach-btn"
                  title="Attach file"
                  @click="$refs.fileInput.click()"
                >
                  <i :class="busy.uploading ? 'fas fa-spinner fa-spin' : 'fas fa-paperclip'" />
                </BButton>
              </div>

              <BInputGroup class="msg-input-group composer-group">
                <BFormInput
                  v-model.trim="draftMessage"
                  placeholder="Type your response..."
                  maxlength="2000"
                  class="message-input"
                  :disabled="busy.sending || busy.uploading"
                  @keyup.enter="sendMessage"
                />
                <BInputGroupAppend>
                  <BButton
                    variant="success"
                    class="send-btn"
                    :disabled="busy.sending || !draftMessage"
                    @click="sendMessage"
                  >
                    <i class="fas fa-paper-plane me-1" />
                    Send
                  </BButton>
                </BInputGroupAppend>
              </BInputGroup>
            </div>

            <div
              v-if="uploadProgress > 0"
              class="upload-progress"
            >
              <div class="progress-bar">
                <div
                  class="progress-fill"
                  :style="{ width: uploadProgress + '%' }"
                />
              </div>
              <small>{{ uploadProgress }}%</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom: Active Sessions Tabs -->
      <div
        v-if="activeSessions.length"
        class="sessions-tabs"
      >
        <div class="tabs-label">
          Active Sessions:
        </div>
        <div class="tabs-list">
          <BButton
            v-for="session in activeSessions"
            :key="`tab-${session.id}`"
            size="sm"
            :variant="selectedSessionId === session.id ? 'success' : 'outline-success'"
            class="tab-btn"
            @click="selectSession(session.id)"
          >
            {{ studentLabel(session.student) }}
          </BButton>
        </div>
      </div>
    </div>

    <BModal
      id="saved-conversations-modal"
      v-model="review.showList"
      title="Saved Counseling Conversations"
      size="lg"
      hide-footer
      body-class="p-0"
    >
      <div class="saved-conversations-panel">
        <div class="saved-conversations-toolbar">
          <BInputGroup size="sm">
            <BFormInput
              v-model.trim="review.search"
              placeholder="Search by student name or ID"
              @keyup.enter="fetchTranscripts(1)"
            />
            <BInputGroupAppend>
              <BButton
                variant="success"
                :disabled="review.loadingList"
                @click="fetchTranscripts(1)"
              >
                <i :class="review.loadingList ? 'fas fa-spinner fa-spin' : 'fas fa-search'" />
              </BButton>
            </BInputGroupAppend>
          </BInputGroup>
        </div>

        <BOverlay
          :show="review.loadingList"
          opacity="0.45"
          rounded="sm"
        >
          <div
            v-if="!review.items.length"
            class="saved-empty"
          >
            <i class="fas fa-folder-open" />
            <p>No saved conversations found.</p>
          </div>

          <div
            v-else
            class="saved-list"
          >
            <div
              v-for="item in review.items"
              :key="`saved-${item.session_id}`"
              class="saved-item"
            >
              <div class="saved-item-head">
                <strong>{{ studentLabel(item.student) }}</strong>
                <span class="saved-date">{{ formatDateTime(item.ended_at) }}</span>
              </div>
              <div class="saved-item-meta">
                <span>{{ item.message_count }} messages</span>
                <span v-if="item.feedback">Feedback: {{ item.feedback.rating }}/5</span>
                <span v-else>No feedback yet</span>
              </div>
              <div class="saved-item-actions">
                <BButton
                  size="sm"
                  variant="outline-success"
                  :disabled="review.loadingDetail"
                  @click="openTranscriptDetail(item.session_id)"
                >
                  <i class="fas fa-eye me-1" />
                  Review
                </BButton>
              </div>
            </div>
          </div>
        </BOverlay>

        <div
          v-if="review.pagination.last_page > 1"
          class="saved-pagination"
        >
          <BPagination
            v-model="review.pagination.current_page"
            :total-rows="review.pagination.total"
            :per-page="review.pagination.per_page"
            size="sm"
            @change="fetchTranscripts"
          />
        </div>
      </div>
    </BModal>

    <BModal
      id="saved-conversation-detail-modal"
      v-model="review.showDetail"
      title="Conversation Review"
      size="xl"
      hide-footer
      body-class="p-0"
    >
      <BOverlay
        :show="review.loadingDetail"
        opacity="0.45"
        rounded="sm"
      >
        <div
          v-if="review.selected"
          class="transcript-detail"
        >
          <div class="transcript-meta">
            <div>
              <strong>Student:</strong>
              {{ studentLabel(review.selected.student) }}
            </div>
            <div>
              <strong>Ended:</strong>
              {{ formatDateTime(review.selected.ended_at) }}
            </div>
            <div>
              <strong>Messages:</strong>
              {{ review.selected.message_count }}
            </div>
            <div>
              <strong>Feedback:</strong>
              {{ formatTranscriptFeedback(review.selected.feedback) }}
            </div>
          </div>

          <div class="transcript-messages">
            <div
              v-if="!review.selected.messages?.length"
              class="saved-empty"
            >
              <i class="fas fa-comment-slash" />
              <p>No transcript messages saved.</p>
            </div>

            <div
              v-else
              class="messages-list"
            >
              <div
                v-for="msg in review.selected.messages"
                :key="`transcript-${msg.id}`"
                :class="['message-item', msg.sender_type === 'admin' ? 'sent' : 'received']"
              >
                <div class="message-bubble">
                  <div class="bubble-header">
                    <span class="bubble-sender">{{ msg.sender_name }}</span>
                    <span class="bubble-time">{{ formatTime(msg.sent_at) }}</span>
                  </div>
                  <div
                    v-if="msg.file_info"
                    class="bubble-text transcript-file"
                  >
                    <i class="fas fa-paperclip me-1" />
                    {{ displayFileName(msg.file_info) }}
                  </div>
                  <div
                    v-if="msg.message"
                    class="bubble-text"
                  >
                    {{ msg.message }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </BOverlay>
    </BModal>

    <BModal
      id="pending-review-modal"
      v-model="pendingReviewPrompt.show"
      title="Save Conversation"
      centered
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      hide-footer
    >
      <div class="pending-review-copy">
        <p class="mb-2">
          The student ended the counseling session.
        </p>
        <p class="mb-0">
          Do you want to save this conversation for review?
        </p>
      </div>

      <div class="pending-review-actions">
        <BButton
          variant="outline-secondary"
          :disabled="pendingReviewPrompt.loading"
          @click="finalizePendingReview(false)"
        >
          No, discard
        </BButton>
        <BButton
          variant="success"
          :disabled="pendingReviewPrompt.loading"
          @click="finalizePendingReview(true)"
        >
          <i :class="pendingReviewPrompt.loading ? 'fas fa-spinner fa-spin me-1' : 'fas fa-save me-1'" />
          Save conversation
        </BButton>
      </div>
    </BModal>
  </div>
</template>

<script>
import { SSharedCounselingChat } from '@/services'

export default {
  name: 'AdminCounselingChat',

  middleware: ['auth', 'admin'],

  metaInfo: () => ({ title: 'Counseling Admin' }),

  data () {
    return {
      chatViewportHeight: null,
      pendingQueue: [],
      activeSessions: [],
      selectedSessionId: null,
      draftMessage: '',
      uploadProgress: 0,
      busy: {
        acceptingId: null,
        sending: false,
        closing: false,
        uploading: false
      },
      poller: null,
      queueSubscription: null,
      sessionSubscriptions: {},
      lastMessageIds: {},
      imagePreviewUrls: {},
      loadingImagePreviewKeys: {},
      review: {
        showList: false,
        showDetail: false,
        loadingList: false,
        loadingDetail: false,
        search: '',
        items: [],
        selected: null,
        pagination: {
          current_page: 1,
          last_page: 1,
          per_page: 8,
          total: 0
        }
      },
      pendingReviewPrompt: {
        show: false,
        loading: false,
        session: null
      },
      handledReviewSessions: {}
    }
  },

  computed: {
    selectedSession () {
      return this.activeSessions.find(item => item.id === this.selectedSessionId) || null
    },

    selectedMessages () {
      return this.selectedSession?.messages || []
    },

    hasActiveSession () {
      return this.activeSessions.length > 0
    }
  },

  watch: {
    selectedMessages: {
      handler () {
        this.$nextTick(() => {
          this.scrollToBottom()
        })

        this.ensureImagePreviews(this.selectedMessages)
      },
      deep: true
    }
  },

  mounted () {
    this.lockChatToViewport()
    window.addEventListener('resize', this.lockChatToViewport, { passive: true })
    this.loadState()
    this.startPolling()
    this.subscribeRealtime()
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.lockChatToViewport)
    this.stopPolling()
    this.unsubscribeRealtime()
    this.cleanupImagePreviews()
  },

  methods: {
    lockChatToViewport () {
      this.$nextTick(() => {
        const root = this.$el
        if (!root || typeof window === 'undefined') return

        const footer = document.querySelector('.iq-footer')
        const footerHeight = footer ? footer.offsetHeight : 0
        const topOffset = Math.max(root.getBoundingClientRect().top, 0)
        const reservedSpace = 28
        const availableHeight = window.innerHeight - topOffset - footerHeight - reservedSpace

        this.chatViewportHeight = availableHeight > 380 ? availableHeight : null
      })
    },

    async loadState () {
      try {
        const { data: payload } = await SSharedCounselingChat.getState()
        this.pendingQueue = payload?.data?.pending_queue || []
        this.activeSessions = payload?.data?.active_sessions || []

        if (!this.selectedSessionId && this.activeSessions.length) {
          this.selectedSessionId = this.activeSessions[0].id
        }

        if (this.selectedSessionId && !this.activeSessions.some(item => item.id === this.selectedSessionId)) {
          this.selectedSessionId = this.activeSessions.length ? this.activeSessions[0].id : null
        }

        // Subscribe to updated session channels
        this.activeSessions.forEach(session => {
          this.subscribeToSession(session.id)
        })

        await this.loadPendingReview()
      } catch (error) {
        console.error('Failed to load state:', error)
      }
    },

    async accept (session) {
      this.busy.acceptingId = session.id
      try {
        await SSharedCounselingChat.acceptSession(session.id)
        await this.loadState()
        this.selectedSessionId = session.id
      } catch (error) {
        if (error.response?.status === 422) {
          this.$bvToast?.toast(
            'You can only handle one active session at a time. Please close your current session first.',
            { variant: 'warning' }
          )
        }
      } finally {
        this.busy.acceptingId = null
      }
    },

    selectSession (sessionId) {
      this.selectedSessionId = sessionId
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },

    async sendMessage (options = null) {
      if (!this.selectedSessionId) return

      const isPayload = Boolean(
        options &&
        options.constructor === Object &&
        (Object.prototype.hasOwnProperty.call(options, 'message') || Object.prototype.hasOwnProperty.call(options, 'fileInfo'))
      )

      const message = isPayload
        ? (typeof options.message === 'string' ? options.message : '')
        : this.draftMessage

      const fileInfo = isPayload
        ? (options.fileInfo || null)
        : null

      if (!message && !fileInfo) return

      const optimisticMessage = {
        id: 'temp-' + Date.now(),
        sender_type: 'admin',
        sender_id: this.$store.state.auth.user?.id || 0,
        sender_name: 'You',
        message: message,
        file_info: fileInfo,
        sent_at: new Date().toISOString(),
        optimistic: true
      }

      // Optimistic update
      const activeSession = this.activeSessions.find(item => item.id === this.selectedSessionId)
      if (activeSession) {
        activeSession.messages.push(optimisticMessage)
      }
      this.draftMessage = ''

      this.busy.sending = true
      try {
        const { data: payload } = await SSharedCounselingChat.sendMessage(this.selectedSessionId, message, fileInfo)
        const idx = activeSession?.messages.findIndex(m => m.id === optimisticMessage.id)
        if (idx !== undefined && idx !== -1 && activeSession) {
          activeSession.messages[idx] = payload?.data?.message
          this.lastMessageIds[this.selectedSessionId] = payload?.data?.message?.id || this.lastMessageIds[this.selectedSessionId]
        }
      } catch (error) {
        // Remove optimistic message on error
        if (activeSession) {
          const idx = activeSession.messages.indexOf(optimisticMessage)
          if (idx !== -1) {
            activeSession.messages.splice(idx, 1)
          }
        }
        this.$bvToast?.toast('Failed to send message', { variant: 'danger' })
      } finally {
        this.busy.sending = false
      }
    },

    async closeSelected () {
      if (!this.selectedSessionId) return

      const confirmed = await this.$bvModal?.msgBoxConfirm('End this counseling session?', {
        title: 'End Session',
        okVariant: 'danger',
        okTitle: 'End Session',
        cancelTitle: 'Cancel'
      })
      if (!confirmed) return

      const saveTranscript = await this.$bvModal?.msgBoxConfirm(
        'Do you want to save this conversation for review?',
        {
          title: 'Save Conversation',
          okVariant: 'success',
          okTitle: 'Save',
          cancelVariant: 'outline-secondary',
          cancelTitle: 'Do Not Save',
          hideHeaderClose: true
        }
      )

      this.busy.closing = true
      try {
        await SSharedCounselingChat.closeSession(this.selectedSessionId, Boolean(saveTranscript))
        await this.loadState()
        this.$bvToast?.toast(
          saveTranscript
            ? 'Session ended and conversation was saved for review.'
            : 'Session ended without saving the conversation.',
          { variant: saveTranscript ? 'success' : 'secondary' }
        )
      } catch (error) {
        this.$bvToast?.toast(error?.response?.data?.message || 'Failed to close session.', { variant: 'danger' })
      } finally {
        this.busy.closing = false
      }
    },

    async loadPendingReview () {
      try {
        const { data: payload } = await SSharedCounselingChat.getPendingReview()
        const reviewSession = payload?.data || null

        if (reviewSession && !this.handledReviewSessions[reviewSession.session_id]) {
          this.pendingReviewPrompt.session = reviewSession
          this.pendingReviewPrompt.show = true
        }
      } catch (error) {
        console.error('Failed to load pending review:', error)
      }
    },

    async finalizePendingReview (keep) {
      if (!this.pendingReviewPrompt.session?.session_id) return

      this.pendingReviewPrompt.loading = true
      try {
        await SSharedCounselingChat.reviewTranscript(this.pendingReviewPrompt.session.session_id, keep)
        this.$set(this.handledReviewSessions, this.pendingReviewPrompt.session.session_id, true)
        this.pendingReviewPrompt.show = false
        this.pendingReviewPrompt.session = null
        this.$bvToast?.toast(
          keep ? 'Conversation saved for review.' : 'Conversation discarded.',
          { variant: keep ? 'success' : 'secondary' }
        )
        await this.loadPendingReview()
      } catch (error) {
        this.$bvToast?.toast(error?.response?.data?.message || 'Failed to finalize transcript review.', { variant: 'danger' })
      } finally {
        this.pendingReviewPrompt.loading = false
      }
    },

    async openTranscriptReview () {
      this.review.showList = true
      this.review.showDetail = false
      this.review.selected = null
      await this.fetchTranscripts(1)
    },

    async fetchTranscripts (page = 1) {
      this.review.loadingList = true
      this.review.pagination.current_page = Number(page) || 1

      try {
        const { data: payload } = await SSharedCounselingChat.getTranscripts({
          page: this.review.pagination.current_page,
          per_page: this.review.pagination.per_page,
          search: this.review.search || undefined
        })

        const root = payload?.data || {}
        this.review.items = root?.items || []

        if (root?.pagination) {
          this.review.pagination = {
            ...this.review.pagination,
            ...root.pagination
          }
        }
      } catch (error) {
        this.review.items = []
        this.$bvToast?.toast(error?.response?.data?.message || 'Failed to load saved conversations.', { variant: 'danger' })
      } finally {
        this.review.loadingList = false
      }
    },

    async openTranscriptDetail (sessionId) {
      this.review.loadingDetail = true
      this.review.selected = null

      try {
        const { data: payload } = await SSharedCounselingChat.getTranscript(sessionId)
        this.review.selected = payload?.data || null
        this.review.showDetail = true
      } catch (error) {
        this.$bvToast?.toast(error?.response?.data?.message || 'Failed to load conversation transcript.', { variant: 'danger' })
      } finally {
        this.review.loadingDetail = false
      }
    },

    async onFileSelected (event) {
      const file = event.target.files?.[0]
      const targetSessionId = this.selectedSessionId || this.selectedSession?.id || this.activeSessions[0]?.id
      if (!file || !targetSessionId) return

      this.selectedSessionId = targetSessionId

      if (file.size > 10 * 1024 * 1024) {
        this.$bvToast?.toast('File too large (max 10MB)', { variant: 'warning' })
        return
      }

      this.busy.uploading = true
      this.uploadProgress = 0

      try {
        const response = await SSharedCounselingChat.uploadFile(
          targetSessionId,
          file,
          progressEvent => {
            this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          }
        )

        if (response.data?.success) {
          const fileInfo = response.data.data
          await this.sendMessage({ message: '', fileInfo })
        }
      } catch (error) {
        this.$bvToast?.toast(error?.response?.data?.message || 'Failed to upload file', { variant: 'danger' })
      } finally {
        this.busy.uploading = false
        this.uploadProgress = 0
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = ''
        }
      }
    },

    startPolling () {
      // Poll frequently so queue/message state stays fresh even if Echo is unavailable.
      this.poller = setInterval(() => {
        if (this.busy.acceptingId || this.busy.sending || this.busy.uploading || this.busy.closing) {
          return
        }

        this.loadState()
      }, 5000)
    },

    stopPolling () {
      if (this.poller) {
        clearInterval(this.poller)
        this.poller = null
      }
    },

    subscribeRealtime () {
      if (!window.Echo) {
        return
      }

      this.unsubscribeRealtime()

      // Subscribe to queue updates
      this.queueSubscription = window.Echo.private('counseling.admin.queue')
        .listen('.counseling.session.requested', () => {
          this.loadState()
        })
        .listen('.counseling.session.accepted', () => {
          this.loadState()
        })
        .listen('.counseling.session.closed', () => {
          this.loadState()
        })
        .listen('.queue.status.updated', () => {
          this.loadState()
        })

      // Subscribe to session updates for active sessions
      this.activeSessions.forEach(session => {
        this.subscribeToSession(session.id)
      })
    },

    subscribeToSession (sessionId) {
      if (!window.Echo || this.sessionSubscriptions[sessionId]) {
        return
      }

      this.sessionSubscriptions[sessionId] = window.Echo.private(`counseling.session.${sessionId}`)
        .listen('.counseling.message.sent', event => {
          if (event?.message && !this.sessionHasMessage(sessionId, event.message.id)) {
            const activeSession = this.activeSessions.find(item => item.id === sessionId)
            if (activeSession) {
              activeSession.messages = [...(activeSession.messages || []), event.message]
              this.lastMessageIds[sessionId] = event.message.id
            }
          }
        })
        .listen('.counseling.session.closed', event => {
          this.loadState()

          if (event?.session?.closed_by_type === 'student') {
            this.loadPendingReview()
          }
        })
    },

    unsubscribeRealtime () {
      if (this.queueSubscription) {
        this.queueSubscription.stopListening('.counseling.session.requested')
        this.queueSubscription.stopListening('.counseling.session.accepted')
        this.queueSubscription.stopListening('.counseling.session.closed')
        this.queueSubscription.stopListening('.queue.status.updated')
        this.queueSubscription = null
      }

      // Unsubscribe from all session subscriptions
      Object.keys(this.sessionSubscriptions).forEach(sessionId => {
        const subscription = this.sessionSubscriptions[sessionId]
        if (subscription) {
          subscription.stopListening('.counseling.message.sent')
          subscription.stopListening('.counseling.session.closed')
        }
      })
      this.sessionSubscriptions = {}
    },

    scrollToBottom () {
      if (this.$refs.chatMessages) {
        this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight
      }
    },

    sessionHasMessage (sessionId, messageId) {
      if (!messageId) return false
      const session = this.activeSessions.find(item => item.id === sessionId)
      return (session?.messages || []).some(message => message.id === messageId)
    },

    extractFileName (fileInfo) {
      const fallbackName = 'attachment'
      const rawName = (fileInfo?.file_name || fileInfo?.stored_name || fallbackName).trim()

      // Remove generated UUID prefix when present in stored filenames.
      return rawName.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i, '')
    },

    truncateFileName (fileName, maxLength = 32) {
      if (!fileName) return 'attachment'
      if (fileName.length <= maxLength) return fileName

      const dotIndex = fileName.lastIndexOf('.')
      if (dotIndex <= 0 || dotIndex === fileName.length - 1) {
        return `${fileName.slice(0, maxLength - 3)}...`
      }

      const extension = fileName.slice(dotIndex)
      const baseName = fileName.slice(0, dotIndex)
      const maxBaseLength = Math.max(8, maxLength - extension.length - 3)

      return `${baseName.slice(0, maxBaseLength)}...${extension}`
    },

    displayFileName (fileInfo) {
      return this.truncateFileName(this.extractFileName(fileInfo))
    },

    isImageAttachment (fileInfo) {
      if (!fileInfo) return false

      const mimeType = (fileInfo.mime_type || '').toLowerCase()
      if (mimeType.startsWith('image/')) return true

      const fileName = this.extractFileName(fileInfo).toLowerCase()
      return /\.(png|jpe?g|gif|bmp|webp|svg)$/.test(fileName)
    },

    filePreviewKey (fileInfo) {
      return fileInfo?.stored_name || fileInfo?.file_url || fileInfo?.file_name || null
    },

    imagePreviewUrl (fileInfo) {
      const key = this.filePreviewKey(fileInfo)
      return key ? this.imagePreviewUrls[key] : null
    },

    openImagePreview (fileInfo) {
      const previewUrl = this.imagePreviewUrl(fileInfo)
      if (previewUrl) {
        window.open(previewUrl, '_blank', 'noopener,noreferrer')
        return
      }

      this.downloadAttachment(fileInfo)
    },

    ensureImagePreviews (messagesList = []) {
      if (!Array.isArray(messagesList)) return

      this.syncImagePreviewCache(messagesList)

      messagesList
        .filter(message => this.isImageAttachment(message.file_info))
        .forEach(message => {
          this.loadImagePreview(message.file_info)
        })
    },

    syncImagePreviewCache (messagesList = []) {
      const activeKeys = new Set(
        messagesList
          .filter(message => this.isImageAttachment(message.file_info))
          .map(message => this.filePreviewKey(message.file_info))
          .filter(Boolean)
      )

      Object.keys(this.imagePreviewUrls).forEach(key => {
        if (!activeKeys.has(key)) {
          window.URL.revokeObjectURL(this.imagePreviewUrls[key])
          this.$delete(this.imagePreviewUrls, key)
        }
      })

      Object.keys(this.loadingImagePreviewKeys).forEach(key => {
        if (!activeKeys.has(key)) {
          this.$delete(this.loadingImagePreviewKeys, key)
        }
      })
    },

    async loadImagePreview (fileInfo) {
      if (!this.selectedSessionId || !fileInfo) return

      const key = this.filePreviewKey(fileInfo)
      if (!key || this.imagePreviewUrls[key] || this.loadingImagePreviewKeys[key]) {
        return
      }

      this.$set(this.loadingImagePreviewKeys, key, true)

      try {
        const storedName = this.resolveStoredName(fileInfo)
        const response = storedName
          ? await SSharedCounselingChat.downloadFile(this.selectedSessionId, storedName)
          : await this.$http.get(fileInfo.file_url, { responseType: 'blob' })

        const objectUrl = window.URL.createObjectURL(response.data)
        this.$set(this.imagePreviewUrls, key, objectUrl)
      } catch (error) {
        // Keep silent; attachment can still be downloaded manually.
      } finally {
        this.$delete(this.loadingImagePreviewKeys, key)
      }
    },

    cleanupImagePreviews () {
      Object.values(this.imagePreviewUrls).forEach(url => {
        window.URL.revokeObjectURL(url)
      })

      this.imagePreviewUrls = {}
      this.loadingImagePreviewKeys = {}
    },

    async downloadAttachment (fileInfo) {
      if (!this.selectedSessionId) {
        this.$bvToast?.toast('No active session for this attachment.', { variant: 'warning' })
        return
      }

      const storedName = this.resolveStoredName(fileInfo)

      if (!storedName && !fileInfo?.file_url) {
        this.$bvToast?.toast('Attachment is unavailable.', { variant: 'warning' })
        return
      }

      try {
        const response = storedName
          ? await SSharedCounselingChat.downloadFile(this.selectedSessionId, storedName)
          : await this.$http.get(fileInfo.file_url, { responseType: 'blob' })

        const blobUrl = window.URL.createObjectURL(new Blob([response.data], {
          type: fileInfo.mime_type || 'application/octet-stream'
        }))

        const link = document.createElement('a')
        link.href = blobUrl
        link.download = this.extractFileName(fileInfo)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)
      } catch (error) {
        this.$bvToast?.toast('Failed to download attachment', { variant: 'danger' })
      }
    },

    resolveStoredName (fileInfo) {
      if (!fileInfo) return null

      if (fileInfo.stored_name) {
        return fileInfo.stored_name
      }

      if (!fileInfo.file_url) {
        return null
      }

      const marker = '/download-file/'
      const markerIndex = fileInfo.file_url.indexOf(marker)
      if (markerIndex === -1) {
        return null
      }

      const afterMarker = fileInfo.file_url.slice(markerIndex + marker.length)
      const rawStoredName = afterMarker.split('?')[0]

      try {
        return decodeURIComponent(rawStoredName)
      } catch (error) {
        return rawStoredName
      }
    },

    formatTime (value) {
      if (!value) return ''
      const date = new Date(value)
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    formatDateTime (value) {
      if (!value) return '-'
      return new Date(value).toLocaleString()
    },

    formatTranscriptFeedback (feedback) {
      if (!feedback) return 'No feedback submitted'

      const type = feedback.feedback_type ? ` (${feedback.feedback_type})` : ''
      return `${feedback.rating}/5${type}`
    },

    formatFileSize (bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },

    timeAgo (timestamp) {
      const date = new Date(timestamp)
      const now = new Date()
      const seconds = Math.floor((now - date) / 1000)

      if (seconds < 60) return 'just now'
      const minutes = Math.floor(seconds / 60)
      if (minutes < 60) return `${minutes}m ago`
      const hours = Math.floor(minutes / 60)
      if (hours < 24) return `${hours}h ago`
      return date.toLocaleDateString()
    },

    studentLabel (student) {
      if (!student) return 'Unknown'
      return `${student.last_name || ''}, ${student.first_name || ''}`.trim()
    },

    getFileIcon (mimeType) {
      if (!mimeType) return 'fas fa-file'
      if (mimeType.startsWith('image/')) return 'fas fa-image'
      if (mimeType.startsWith('video/')) return 'fas fa-video'
      if (mimeType.startsWith('audio/')) return 'fas fa-file-audio'
      if (mimeType.includes('pdf')) return 'fas fa-file-pdf'
      if (mimeType.includes('word') || mimeType.includes('document')) return 'fas fa-file-word'
      if (mimeType.includes('sheet') || mimeType.includes('excel') || mimeType.includes('csv')) return 'fas fa-file-excel'
      if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'fas fa-file-archive'
      if (mimeType.includes('text') || mimeType.includes('json')) return 'fas fa-file-alt'
      return 'fas fa-file'
    }
  }
}
</script>

<style scoped lang="scss">
.counseling-admin-container {
  --admin-chat-shell-height: calc(100dvh - 185px);
  display: flex;
  flex-direction: column;
  height: var(--admin-chat-shell-height);
  max-height: var(--admin-chat-shell-height);
  min-height: 0;
  background: #f5f5f5;
  overflow: hidden;
}

.admin-header {
  padding: 20px;
  background: white;
  border-bottom: 2px solid #ddd;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  .header-left {
   .header-title {
      margin: 0 0 4px;
      color: #333;
    }

    .header-subtitle {
      margin: 0;
      color: #999;
      font-size: 0.875rem;
    }
  }

  .header-stats {
    display: flex;
    gap: 24px;
    align-items: center;

    .stat-item {
      display: flex;
      flex-direction: column;
      align-items: center;

      .stat-label {
        font-size: 0.8rem;
        color: #999;
      }

      .stat-value {
        font-size: 1.5rem;
        font-weight: bold;
        color: #333;
      }
    }

    .review-btn {
      white-space: nowrap;
    }
  }
}

.admin-layout {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  gap: 1px;
}

.queue-panel {
  width: 300px;
  background: white;
  border-right: 1px solid #ddd;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .panel-header {
    padding: 16px;
    background: #f9f9f9;
    border-bottom: 1px solid #ddd;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .panel-title {
      margin: 0;
      color: #333;
      font-size: 1rem;
    }

    .pending-badge {
      background: #ffc107;
      color: white;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 0.75rem;
      font-weight: bold;
    }
  }

  .pending-list {
    flex: 1;
    overflow-y: auto;
    padding: 8px;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: #ccc;
      border-radius: 3px;
    }
  }

  .empty-queue {
    text-align: center;
    padding: 40px 20px;
    color: #999;

    i {
      font-size: 2rem;
      margin-bottom: 12px;
      display: block;
      color: #28a745;
    }
  }

  .pending-item {
    padding: 12px;
    margin-bottom: 8px;
    background: #f9f9f9;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    transition: all 0.2s;

    &:hover:not(.disabled) {
      background: white;
      border-color: #2d6a4f;
      box-shadow: 0 2px 8px rgba(45, 106, 79, 0.14);
    }

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .pending-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;

      .student-info {
        display: flex;
        gap: 8px;
        align-items: center;

        .student-name {
          font-weight: 600;
          color: #333;
          font-size: 0.9rem;
        }

        .queue-badge {
          background: #2d6a4f;
          color: white;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: bold;
        }
      }

      .pending-time {
        font-size: 0.75rem;
        color: #999;
      }
    }

    .pending-details {
      font-size: 0.8rem;
      margin-bottom: 8px;

      .detail-row {
        display: flex;
        gap: 6px;
        margin-bottom: 4px;

        .detail-label {
          color: #999;
          min-width: 35px;
        }

        .detail-value {
          color: #333;
          font-weight: 500;
        }
      }
    }

    .accept-btn {
      width: 100%;
      font-size: 0.85rem;
    }
  }
}

.chat-panel {
  flex: 1;
  min-height: 0;
  background: white;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .no-selection {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #999;

    i {
      font-size: 3rem;
      margin-bottom: 16px;
      opacity: 0.5;
    }

    h5 {
      margin-bottom: 8px;
      color: #333;
    }
  }

  .session-chat {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .session-header {
    padding: 16px;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: white;

    .session-info {
      .session-title {
        margin: 0;
        color: #333;
        font-size: 1rem;
      }

      .session-subtitle {
        margin: 4px 0 0;
        color: #999;
        font-size: 0.85rem;
      }
    }
  }

  .chat-messages {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-thumb {
      background: #ccc;
      border-radius: 4px;
    }
  }

  .messages-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #999;

    i {
      font-size: 2rem;
      margin-bottom: 12px;
    }
  }

  .messages-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .message-item {
    display: flex;
    margin-bottom: 4px;

    &.sent {
      justify-content: flex-end;

      .message-bubble {
        background: #2d6a4f;
        color: white;
      }
    }

    &.received {
      justify-content: flex-start;

      .message-bubble {
        background: #f0f0f0;
        color: #333;
      }
    }
  }

  .message-bubble {
    max-width: 70%;
    padding: 10px 14px;
    border-radius: 10px;
    animation: slideIn 0.3s ease-out;

    .bubble-header {
      display: flex;
      justify-content: space-between;
      gap: 8px;
      margin-bottom: 4px;
      font-size: 0.75rem;
      opacity: 0.8;

      .bubble-sender {
        font-weight: 600;
      }

      .bubble-time {
        white-space: nowrap;
      }
    }

    .bubble-text {
      line-height: 1.4;
    }

    .bubble-file {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-bottom: 4px;
      font-size: 0.85rem;

      .image-preview-wrapper {
        .image-preview {
          display: block;
          max-width: 240px;
          max-height: 190px;
          border-radius: 8px;
          cursor: pointer;
          object-fit: cover;
          border: 1px solid rgba(255, 255, 255, 0.28);
        }

        .image-preview-loading {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 0.78rem;
          background: rgba(0, 0, 0, 0.12);
        }
      }

      .file-row {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 10px;
        background: rgba(0, 0, 0, 0.12);
        border-radius: 8px;

        .file-type-icon {
          flex-shrink: 0;
        }

        .file-link {
          color: inherit;
          text-decoration: none;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0;
          flex: 1;

          &:hover {
            text-decoration: underline;
          }
        }

        .file-size {
          font-size: 0.72rem;
          opacity: 0.9;
          white-space: nowrap;
        }

        .download-btn {
          border: none;
          background: transparent;
          color: inherit;
          cursor: pointer;
          padding: 2px 4px;
          line-height: 1;
          opacity: 0.9;

          &:hover {
            opacity: 1;
          }
        }
      }
    }
  }

  .chat-input {
    padding: 12px 16px;
    background: white;
    border-top: 1px solid #e0e0e0;

    .input-wrapper {
      display: flex;
      gap: 8px;
      align-items: center;

      .attach-upload {
        display: inline-flex;
        align-items: center;
        flex-shrink: 0;

        .attach-btn {
          width: 40px;
          height: 40px;
          min-width: 40px;
          min-height: 40px;
          padding: 0;
          border-radius: 6px;
          border-color: #f0b448;
          background: #fff9ee;
          color: #d29a2f;
        }

        .file-input-hidden {
          display: none;
        }
      }

      .msg-input-group {
        flex: 1;
        margin-bottom: 0;

        ::v-deep .input-group-append {
          margin-left: 0;
        }

        .message-input {
          height: 40px;
          border-radius: 6px 0 0 6px;
          border: 1px solid #d9dee7;
          border-right: 0;
          background: #f4f6fa;
          padding: 0 12px;
          font-size: 0.92rem;

          &:focus {
            border-color: #2d6a4f;
            box-shadow: none;
            background: #ffffff;
          }
        }

        .send-btn {
          height: 40px;
          border-radius: 0 6px 6px 0;
          padding: 0 16px;
          font-weight: 600;
          border-color: #10b981;
        }
      }
    }

    .upload-progress {
      margin-top: 8px;
      font-size: 0.75rem;

      .progress-bar {
        height: 3px;
        background: #e0e0e0;
        border-radius: 2px;
        overflow: hidden;

        .progress-fill {
          height: 100%;
          background: #2d6a4f;
          transition: width 0.3s ease;
        }
      }
    }
  }
}

.sessions-tabs {
  padding: 12px 16px;
  background: white;
  border-top: 1px solid #ddd;
  display: flex;
  gap: 12px;
  align-items: center;
  overflow-x: auto;

  .tabs-label {
    font-weight: 600;
    white-space: nowrap;
    color: #333;
  }

  .tabs-list {
    display: flex;
    gap: 8px;
    overflow-x: auto;

    .tab-btn {
      white-space: nowrap;
      flex-shrink: 0;
    }
  }
}

.saved-conversations-panel {
  display: flex;
  flex-direction: column;
  max-height: 72vh;

  .saved-conversations-toolbar {
    padding: 12px 16px;
    border-bottom: 1px solid #e5e7eb;
    background: #f8fafc;
  }

  .saved-list {
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 54vh;
    overflow-y: auto;
  }

  .saved-item {
    border: 1px solid #dbe4dd;
    border-radius: 8px;
    background: #fff;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .saved-item-head {
    display: flex;
    justify-content: space-between;
    gap: 10px;

    .saved-date {
      color: #64748b;
      font-size: 0.8rem;
      white-space: nowrap;
    }
  }

  .saved-item-meta {
    display: flex;
    gap: 12px;
    font-size: 0.82rem;
    color: #52606d;
    flex-wrap: wrap;
  }

  .saved-item-actions {
    display: flex;
    justify-content: flex-end;
  }

  .saved-empty {
    text-align: center;
    color: #8a9a8f;
    padding: 28px 16px;

    i {
      display: block;
      font-size: 1.8rem;
      margin-bottom: 8px;
    }

    p {
      margin: 0;
    }
  }

  .saved-pagination {
    padding: 8px 16px 12px;
    border-top: 1px solid #edf2f7;
    display: flex;
    justify-content: flex-end;
  }
}

.transcript-detail {
  display: flex;
  flex-direction: column;
  max-height: 75vh;

  .transcript-meta {

.pending-review-copy {
  padding: 4px 2px 16px;
  color: #334155;
}

.pending-review-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
    padding: 12px 16px;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    gap: 8px;
    font-size: 0.85rem;
  }

  .transcript-messages {
    flex: 1;
    overflow-y: auto;
    padding: 14px 16px;
    background: #f8fafc;

    .messages-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .message-item {
      display: flex;

      &.sent {
        justify-content: flex-end;

        .message-bubble {
          background: #2d6a4f;
          color: #fff;
        }
      }

      &.received {
        justify-content: flex-start;

        .message-bubble {
          background: #e7ece9;
          color: #253137;
        }
      }
    }

    .message-bubble {
      max-width: min(680px, 86%);
      padding: 10px 12px;
      border-radius: 10px;

      .transcript-file {
        margin-bottom: 4px;
        opacity: 0.92;
      }
    }
  }
}

.pending-review-copy {
  padding: 4px 2px 16px;
  color: #334155;
}

.pending-review-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1024px) {
  .queue-panel {
    width: 250px;
  }
}

@media (max-width: 768px) {
  .counseling-admin-container {
    --admin-chat-shell-height: calc(100dvh - 170px);
  }

  .admin-layout {
    flex-direction: column;
  }

  .queue-panel {
    width: 100%;
    max-height: 30vh;
    border-right: none;
    border-bottom: 1px solid #ddd;
  }

  .message-bubble {
    max-width: 85%;
  }
}
</style>
